// Bridge between the configurator promo popup (mounted inside the route tree)
// and FloatingMessenger (mounted once in Layout, owns the webchat instance).
// The popup can't reach the useWebchat hook directly, so it publishes a
// request here; FloatingMessenger subscribes and drives the chat.

export type PromoChatRequest = {
  /** Shareable configurator link that must reach the manager. */
  projectUrl: string
  /** Phone entered in the popup — the whole lead in one request. */
  phone: string
}

const PHONE_LEFT_KEY = 'webchat_phone_left'
const CONSENT_LOG_KEY = 'promo_popup_consent'

type Listener = (req: PromoChatRequest) => void

let listener: Listener | null = null
// The chat surfaces only once its socket connects; a CTA click before that
// would be lost, so keep the last unconsumed request for the late subscriber.
let pending: PromoChatRequest | null = null

export const promoChatBus = {
  /** Mirrored by FloatingMessenger so the popup can respect an open panel. */
  state: {
    panelOpen: false,
    /** Chat teaser and promo popup are mutually exclusive on screen. */
    teaserVisible: false,
    popupVisible: false,
  },

  request(req: PromoChatRequest): void {
    if (listener) listener(req)
    else pending = req
  },

  subscribe(fn: Listener): () => void {
    listener = fn
    if (pending) {
      const req = pending
      pending = null
      fn(req)
    }
    return () => {
      if (listener === fn) listener = null
    }
  },
}

/** True when the visitor already left a phone in any chat flow (persistent). */
export function hasLeftPhone(): boolean {
  try {
    return !!localStorage.getItem(PHONE_LEFT_KEY)
  } catch {
    return false
  }
}

export function markPhoneLeft(): void {
  try {
    localStorage.setItem(PHONE_LEFT_KEY, '1')
  } catch {
    /* ignore */
  }
}

/** GDPR trace for the promo flow: where and when consent was given. */
export function recordPromoConsent(): void {
  try {
    localStorage.setItem(
      CONSENT_LOG_KEY,
      JSON.stringify({ source: 'promo_popup', at: new Date().toISOString() })
    )
  } catch {
    /* ignore */
  }
}
